import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { apiFetch } from "../../config/api";
import "./index.css";

const EMPTY_WINDOW = {
  date: "",
  start_time: "",
  end_time: "",
};

const STATUS_LABELS = {
  UNS: "Unscheduled",
  SCH: "Scheduled",
  OTW: "On The Way",
  ONS: "On Site",
  CMP: "Complete",
};

const DURATION_OPTIONS = [
  ["", "No window"],
  ["1HR", "1 Hour"],
  ["2HR", "2 Hours"],
  ["3HR", "3 Hours"],
  ["4HR", "4 Hours"],
  ["5HR", "5 Hours"],
  ["6HR", "6 Hours"],
  ["7HR", "7 Hours"],
  ["8HR", "8 Hours"],
];

const pad = (value) => String(value).padStart(2, "0");

const toDateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const startOfWeek = (date) => {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = copy.getDay();
  copy.setDate(copy.getDate() - day);
  return copy;
};

const addDays = (date, days) => {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  copy.setDate(copy.getDate() + days);
  return copy;
};

const formatShortDay = (dateKey) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const normalizeTime = (value) => (value ? value.slice(0, 5) : "");

const parseTimeWindow = (value) => {
  if (!value) {
    return EMPTY_WINDOW;
  }

  if (typeof value === "object") {
    return {
      date: value.date || "",
      start_time: normalizeTime(value.start_time),
      end_time: normalizeTime(value.end_time),
    };
  }

  try {
    const parsed = JSON.parse(value);
    return {
      date: parsed.date || "",
      start_time: normalizeTime(parsed.start_time),
      end_time: normalizeTime(parsed.end_time),
    };
  } catch (error) {
    return EMPTY_WINDOW;
  }
};

const serializeTimeWindow = ({ date, start_time, end_time }) =>
  JSON.stringify({
    date: date || null,
    start_time: start_time ? `${start_time}:00` : null,
    end_time: end_time ? `${end_time}:00` : null,
  });

const getTripTitle = (trip) => {
  const summary = trip.job_summary || {};
  return summary.job_number || `Job ${summary.id || trip.job}`;
};

const getCustomerName = (trip) => {
  const summary = trip.job_summary || {};
  return summary.bill_to_name || summary.client_name || "No customer name";
};

const buildJobDraft = (job) => ({
  id: `job-${job.id}`,
  isJobDraft: true,
  job: job.id,
  action_item: job.scope_of_work || "",
  scheduled_date: "",
  duration: "",
  status: "UNS",
  assignments: [],
  job_summary: {
    id: job.id,
    job_number: job.job_number,
    status: job.status,
    client_name: job.client?.name || "",
    bill_to_name: job.bill_to_name || "",
    job_site_address: job.job_site_address || "",
    scope_of_work: job.scope_of_work || "",
  },
});

const DispatchPage = () => {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [employees, setEmployees] = useState([]);
  const [trips, setTrips] = useState([]);
  const [unscheduledTrips, setUnscheduledTrips] = useState([]);
  const [jobsWithoutTrips, setJobsWithoutTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [formData, setFormData] = useState({
    date: "",
    start_time: "",
    end_time: "",
    duration: "",
    action_item: "",
    assigned_employee_ids: [],
  });
  const [conflictWarnings, setConflictWarnings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => toDateKey(addDays(weekStart, index))),
    [weekStart]
  );

  const fetchDispatchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const endOfWeek = addDays(weekStart, 6);
      const [employeeResponse, tripsResponse, unscheduledResponse, jobsResponse] =
        await Promise.all([
          apiFetch("/api/employees/"),
          apiFetch("/api/trips/", {
            params: {
              start_date: toDateKey(weekStart),
              end_date: toDateKey(endOfWeek),
            },
          }),
          apiFetch("/api/trips/", { params: { scheduled: "0" } }),
          apiFetch("/api/jobs/"),
        ]);

      const scheduledTrips = tripsResponse.data || [];
      const unscheduled = unscheduledResponse.data || [];
      const noTripJobs = (jobsResponse.data || [])
        .filter((job) => !["CMP", "CNL", "INV", "IPD"].includes(job.status))
        .filter((job) => !job.trips || job.trips.length === 0)
        .map(buildJobDraft);

      setEmployees(employeeResponse.data || []);
      setTrips(scheduledTrips);
      setUnscheduledTrips(unscheduled);
      setJobsWithoutTrips(noTripJobs);
    } catch (error) {
      toast.error("Dispatch calendar failed to load.");
    } finally {
      setIsLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchDispatchData();
  }, [fetchDispatchData]);

  const openEditor = (trip, dateOverride = "") => {
    const window = parseTimeWindow(trip.scheduled_date);
    setSelectedTrip(trip);
    setConflictWarnings([]);
    setFormData({
      date: dateOverride || window.date || toDateKey(weekStart),
      start_time: window.start_time,
      end_time: window.end_time,
      duration: trip.duration || "",
      action_item: trip.action_item || "",
      assigned_employee_ids: (trip.assignments || []).map((assignment) => assignment.employee),
    });
  };

  const closeEditor = () => {
    setSelectedTrip(null);
    setConflictWarnings([]);
  };

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const updateAssignedEmployees = (event) => {
    const assignedEmployeeIds = Array.from(event.target.selectedOptions).map((option) =>
      Number(option.value)
    );
    updateField("assigned_employee_ids", assignedEmployeeIds);
  };

  const checkConflicts = async () => {
    const response = await apiFetch("/api/trips/conflicts/", {
      method: "POST",
      body: {
        trip: selectedTrip?.isJobDraft ? null : selectedTrip?.id,
        scheduled_date: serializeTimeWindow(formData),
        assigned_employee_ids: formData.assigned_employee_ids,
      },
    });
    setConflictWarnings(response.data?.warnings || []);
    return response.data?.warnings || [];
  };

  const saveTrip = async (event) => {
    event.preventDefault();
    try {
      await checkConflicts();
      const body = {
        action_item: formData.action_item,
        scheduled_date: serializeTimeWindow(formData),
        duration: formData.duration || null,
        assigned_employee_ids: formData.assigned_employee_ids,
        status: formData.date ? "SCH" : selectedTrip.status,
      };

      if (selectedTrip.isJobDraft) {
        await apiFetch("/api/trips/", {
          method: "POST",
          body: {
            ...body,
            job: selectedTrip.job,
          },
        });
      } else {
        await apiFetch(`/api/trips/${selectedTrip.id}/`, {
          method: "PATCH",
          body,
        });
      }

      toast.success("Dispatch updated.");
      closeEditor();
      fetchDispatchData();
    } catch (error) {
      toast.error("Dispatch update failed.");
    }
  };

  const tripsByDateAndEmployee = useMemo(() => {
    const grouped = new Map();
    trips.forEach((trip) => {
      const window = parseTimeWindow(trip.scheduled_date);
      if (!window.date) {
        return;
      }
      (trip.assignments || []).forEach((assignment) => {
        const key = `${window.date}:${assignment.employee}`;
        if (!grouped.has(key)) {
          grouped.set(key, []);
        }
        grouped.get(key).push(trip);
      });
    });
    return grouped;
  }, [trips]);

  const queueItems = useMemo(() => {
    const queuedTrips = new Map();
    [...unscheduledTrips, ...trips.filter((trip) => !(trip.assignments || []).length)].forEach(
      (trip) => queuedTrips.set(`trip-${trip.id}`, trip)
    );
    jobsWithoutTrips.forEach((job) => queuedTrips.set(job.id, job));
    return Array.from(queuedTrips.values());
  }, [jobsWithoutTrips, trips, unscheduledTrips]);

  return (
    <main className="dispatch-page">
      <section className="dispatch-toolbar">
        <div>
          <h1>Dispatch</h1>
          <p>{formatShortDay(weekDays[0])} to {formatShortDay(weekDays[6])}</p>
        </div>
        <div className="dispatch-toolbar-actions">
          <button type="button" onClick={() => setWeekStart(addDays(weekStart, -7))}>
            Previous
          </button>
          <button type="button" onClick={() => setWeekStart(startOfWeek(new Date()))}>
            Today
          </button>
          <button type="button" onClick={() => setWeekStart(addDays(weekStart, 7))}>
            Next
          </button>
        </div>
      </section>

      {isLoading ? (
        <div className="dispatch-loading">Loading dispatch calendar...</div>
      ) : (
        <div className="dispatch-layout">
          <aside className="dispatch-queue">
            <div className="dispatch-panel-heading">
              <h2>Unscheduled</h2>
              <span>{queueItems.length}</span>
            </div>
            <div className="dispatch-queue-list">
              {queueItems.map((trip) => (
                <button
                  type="button"
                  className="dispatch-queue-item"
                  key={trip.id}
                  onClick={() => openEditor(trip)}
                >
                  <strong>{getTripTitle(trip)}</strong>
                  <span>{getCustomerName(trip)}</span>
                  <small>{trip.job_summary?.job_site_address || "No job address"}</small>
                </button>
              ))}
              {!queueItems.length && (
                <div className="dispatch-empty">No unscheduled work.</div>
              )}
            </div>
          </aside>

          <section className="dispatch-calendar" aria-label="Weekly dispatch calendar">
            <div className="dispatch-grid dispatch-grid-header">
              <div className="dispatch-corner">Technician</div>
              {weekDays.map((day) => (
                <div className="dispatch-day-heading" key={day}>
                  {formatShortDay(day)}
                </div>
              ))}
            </div>

            {employees.map((employee) => (
              <div className="dispatch-grid dispatch-row" key={employee.id}>
                <div className="dispatch-employee">{employee.name}</div>
                {weekDays.map((day) => {
                  const dayTrips = tripsByDateAndEmployee.get(`${day}:${employee.id}`) || [];
                  return (
                    <div className="dispatch-cell" key={`${employee.id}-${day}`}>
                      {dayTrips.map((trip) => {
                        const window = parseTimeWindow(trip.scheduled_date);
                        return (
                          <button
                            type="button"
                            className="dispatch-trip-card"
                            key={`${trip.id}-${employee.id}`}
                            onClick={() => openEditor(trip)}
                          >
                            <span className="dispatch-trip-time">
                              {window.start_time || "Any"}-{window.end_time || "time"}
                            </span>
                            <strong>{getTripTitle(trip)}</strong>
                            <span>{getCustomerName(trip)}</span>
                            <small>{trip.job_summary?.job_site_address || "No address"}</small>
                            <em>{STATUS_LABELS[trip.status] || trip.status}</em>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </section>
        </div>
      )}

      <Modal
        isOpen={!!selectedTrip}
        onRequestClose={closeEditor}
        className="dispatch-modal"
        overlayClassName="dispatch-modal-overlay"
        contentLabel="Edit dispatch trip"
      >
        {selectedTrip && (
          <form onSubmit={saveTrip}>
            <div className="dispatch-modal-header">
              <div>
                <h2>{getTripTitle(selectedTrip)}</h2>
                <p>{getCustomerName(selectedTrip)}</p>
              </div>
              <button type="button" onClick={closeEditor} aria-label="Close dispatch editor">
                X
              </button>
            </div>

            <div className="dispatch-form-grid">
              <label>
                Date
                <input
                  type="date"
                  value={formData.date}
                  onChange={(event) => updateField("date", event.target.value)}
                />
              </label>
              <label>
                Start
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(event) => updateField("start_time", event.target.value)}
                />
              </label>
              <label>
                End
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(event) => updateField("end_time", event.target.value)}
                />
              </label>
              <label>
                Window
                <select
                  value={formData.duration}
                  onChange={(event) => updateField("duration", event.target.value)}
                >
                  {DURATION_OPTIONS.map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="dispatch-full-field">
              Technicians
              <select
                multiple
                value={formData.assigned_employee_ids.map(String)}
                onChange={updateAssignedEmployees}
              >
                {employees.map((employee) => (
                  <option value={employee.id} key={employee.id}>
                    {employee.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="dispatch-full-field">
              Action item
              <textarea
                value={formData.action_item}
                onChange={(event) => updateField("action_item", event.target.value)}
                rows="4"
              />
            </label>

            {conflictWarnings.length > 0 && (
              <div className="dispatch-conflicts">
                <strong>Schedule warning</strong>
                {conflictWarnings.map((warning) => (
                  <div key={`${warning.employee}-${warning.trip}`}>
                    {warning.employee_name} overlaps with {warning.job_number || `job ${warning.job}`}.
                  </div>
                ))}
              </div>
            )}

            <div className="dispatch-modal-actions">
              <button type="button" onClick={checkConflicts}>
                Check Conflicts
              </button>
              <button type="submit">Save Dispatch</button>
            </div>
          </form>
        )}
      </Modal>
    </main>
  );
};

export default DispatchPage;
